import { useEffect, useRef } from 'react';
import {
  canCallEndpoint,
  clearEndpointTTLData,
  getLocationFromUrl,
  markEndpointAsCalled,
  setLocationFromUrl
} from '../../../store/componentStores/utilities/endpointTtlValidation';

const MAX_ENDPOINT_CALL_ATTEMPTS = 2;
const MAX_TTL_CLEAR_RUNS_BEFORE_OCCUPANCY_LOADED = 2;

const useEndpointValidation = (maxOccupancy, endpointList, resetCallback, resortLocation) => {
  const effectRunCount = useRef(0);
  const endpointAttemptCounts = useRef({}); // { [endpointName]: count of dispatched attempts not yet succeeded }
  const url = window.location.href;

  useEffect(() => {
    const locationFromUrl = getLocationFromUrl();

    if (!url.includes(locationFromUrl)) {
      endpointAttemptCounts.current = {};
      if (typeof resetCallback === 'function') {
        clearEndpointTTLData(endpointList);
        resetCallback();
        setLocationFromUrl();
      }
    }

    // Iterate over endpointList
    for (const endpoint of endpointList) {
      const { name, TTL, validateUrl, isLodgeSpecific } = endpoint;

      if (!isLodgeSpecific || resortLocation) {
        const attempts = endpointAttemptCounts.current[name] ?? 0;
        if (
          canCallEndpoint(name, TTL, validateUrl) &&
          typeof endpoint.call === 'function' &&
          attempts <= MAX_ENDPOINT_CALL_ATTEMPTS
        ) {
          // Call the validated endpoint and only mark it as called when it reports
          // success; on failure leave it unmarked so it can be retried.
          endpointAttemptCounts.current[name] = attempts + 1; // count the dispatch synchronously, before awaiting
          endpoint
            .call()
            .then(succeeded => {
              if (succeeded) {
                markEndpointAsCalled(name);
                endpointAttemptCounts.current[name] = 0; // success clears the cap for this endpoint
              }
            })
            .catch(() => {}); // no increment here — the dispatch was already counted above
        }
      } else {
        // Left following log as commented code to assist in future debugging in case if LodgeConfig data is not
        // getting loaded properly during initial load of application
        // console.log('endpoint', name, 'is lodge specific but no resort location provided. Skipping call');
      }
    }
    effectRunCount.current += 1;
    const shouldClearEndpointTTLData =
      !maxOccupancy && effectRunCount.current < MAX_TTL_CLEAR_RUNS_BEFORE_OCCUPANCY_LOADED;
    if (shouldClearEndpointTTLData) clearEndpointTTLData(endpointList);
  }, [endpointList, resetCallback, resortLocation, url, maxOccupancy]);
};

export default useEndpointValidation;
